"use client";

import React, { useEffect, useRef } from "react";
import {
  Avatar,
  Button,
  ModalBody,
  Modal,
  ModalContent,
  ModalHeader,
  Tooltip,
  useDisclosure,
  Textarea,
  Link,
} from "@heroui/react";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from "@heroui/dropdown";
// import { Kbd } from "@heroui/kbd";
// import { Input } from "@heroui/input";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth, useUser, usePermissions } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
// import { SearchIcon } from "@/components/icons";
import { ThemeSwitch } from "@/components/theme-switch";
import { availableLanguages } from "@/i18n";
import { useTheme } from "@/hooks/use-theme";
import { useGlobalStore } from "@/stores/GlobalStore";
import { useChatStore } from "@/stores/ChatStore";
import { useChatHistoryStore } from "@/stores/ChatHistoryStore";
import { useChatURLState } from "@/hooks/useChatURLState";
import { logger } from "@/telemetry";

import { ModelInfoIcon } from "../icons";
import { ChatHistorySearchModal } from "../Chat/ChatHistorySearchModal";
import { SearchChatButton } from "../Chat/SearchChatButton";
import { ChatHistoryComponent } from "../Chat/ChatHistory";
import { I18nIcon, LanguageSwitch } from "../language-switch";

import { useSidebar } from "./SidebarContext";
import { useSidebarMenuItems } from "./SidebarMenuItems";
import Sidebar from "./Sidebar";

// function AvatarDropdownIcon(props: ComponentProps<"svg">) {
//   return (
//     <svg
//       {...props}
//       fill="none"
//       height="20"
//       viewBox="0 0 20 20"
//       width="20"
//       xmlns="http://www.w3.org/2000/svg"
//     >
//       <g clipPath="url(#clip0_3076_10614)">
//         <path
//           d="M6.6665 7.50008L9.99984 4.16675L13.3332 7.50008"
//           stroke="#A1A1AA"
//           strokeLinecap="round"
//           strokeLinejoin="round"
//         />
//         <path
//           d="M13.3332 12.5L9.99984 15.8333L6.6665 12.5"
//           stroke="#A1A1AA"
//           strokeLinecap="round"
//           strokeLinejoin="round"
//         />
//       </g>
//       <defs>
//         <clipPath id="clip0_3076_10614">
//           <rect fill="white" height="20" width="20" />
//         </clipPath>
//       </defs>
//     </svg>
//   );
// }

export function SidebarLayout({ children }: { children: React.ReactNode }) {
  // Auth and user hooks
  const { userProfile } = useUser();
  const { user, isAuthenticated } = useAuth();
  const { logout } = useAuth();
  const { permissions } = usePermissions();
  const { adminClient } = useWebuildAPIClient();

  // Store hooks
  const { newChat, load, triggerResetPrompt } = useChatStore();
  const {
    fetchChatHistory,
    displayedChats,
    chatHistory,
    selectChat,
    selectAndEnsureVisible,
    clearSelection,
  } = useChatHistoryStore();
  const { sidebar } = useGlobalStore();
  const { isSidebarCompact, toggleSidebarCompact } = sidebar;

  // Navigation hooks
  const location = useLocation();
  const navigate = useNavigate();
  const { workflowId: urlWorkflowId } = useParams<{ workflowId?: string }>();
  const { navigateToChat, clearChatId, chatId: chatIdFromURL } = useChatURLState();

  // UI hooks
  const { t } = useTranslation();
  const { showChatHistory, setShowChatHistory } = useSidebar();

  // Derived values
  const pathname = location.pathname;
  const currentPath = pathname.startsWith("/workflows/playground")
    ? "playground"
    : pathname.split("/")?.[1];

  // Determine the current workflow ID from URL or default to chat_agent
  const currentWorkflowId = urlWorkflowId || "chat_agent";

  useEffect(() => {
    const shouldShowHistory = location.pathname.startsWith("/chat");

    if (shouldShowHistory !== showChatHistory) {
      setShowChatHistory(shouldShowHistory);
    }
  }, [location.pathname, showChatHistory, setShowChatHistory]);

  // Track the last chat ID we handled from URL to avoid re-processing on displayedChats changes
  const lastHandledUrlChatIdRef = useRef<string | null>(null);

  // Select chat from URL when chat history is loaded (initial page load only)
  useEffect(() => {
    // Skip if no chat ID in URL or already handled this chat ID
    if (!chatIdFromURL || !adminClient || displayedChats.length === 0) {
      return;
    }

    // Skip if we already handled this chat ID (prevents re-running on displayedChats changes)
    if (lastHandledUrlChatIdRef.current === chatIdFromURL) {
      return;
    }

    logger.info("🔗 SidebarLayout: URL has chat ID, selecting and loading", { chatIdFromURL });
    lastHandledUrlChatIdRef.current = chatIdFromURL;

    // Use selectAndEnsureVisible to fetch the chat if it's not in the displayed list
    selectAndEnsureVisible(chatIdFromURL, adminClient);

    // Load the chat data if it's not already loaded
    load(adminClient, chatIdFromURL);
  }, [chatIdFromURL, displayedChats.length, selectAndEnsureVisible, adminClient, load]);

  const handleBackToMenu = () => {
    logger.info("🔙 Back button clicked - navigating back in history");

    // Clear chat selection
    clearSelection();

    // Use browser's back navigation to go to previous page
    navigate(-1);
  };

  // Debug logging
  logger.info("SidebarLayout Debug", {
    showChatHistory: String(showChatHistory),
    isSidebarCompact: String(isSidebarCompact),
    pathname,
    displayedChatsCount: displayedChats.length,
  });

  const {
    isOpen: isSearchModalOpen,
    onOpen: onSearchModalOpen,
    onOpenChange: onSearchModalOpenChange,
  } = useDisclosure();

  // Keyboard shortcut: Cmd+K (Mac) / Ctrl+K (Windows/Linux) to open search
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      logger.info("SidebarLayout keydown", {
        key: e.key,
        metaKey: String(e.metaKey),
        ctrlKey: String(e.ctrlKey),
        showChatHistory: String(showChatHistory),
        displayedChatsLength: displayedChats.length,
        activeElement: document.activeElement?.tagName,
      });

      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      // Check both lowercase and uppercase 'k'/'K'
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        logger.info("🔍 Ctrl/Cmd+K detected, checking conditions", {
          showChatHistory: String(showChatHistory),
          displayedChatsLength: displayedChats.length,
          shouldOpen: String(showChatHistory && displayedChats.length > 0),
        });

        // Only trigger if on chat page with history visible
        if (showChatHistory && displayedChats.length > 0) {
          e.preventDefault(); // Prevent browser default (Chrome address bar focus)
          e.stopPropagation();
          logger.info("✅ Opening search modal via keyboard shortcut");
          onSearchModalOpen();
        }
      }
    };

    // Use capture phase to intercept events before they reach child elements
    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [showChatHistory, displayedChats.length, onSearchModalOpen]);

  const handleSelectChat = async (chatHistoryId: string) => {
    if (!adminClient) return;

    // Mark this chat ID as handled BEFORE navigation to prevent URL effect from re-processing
    lastHandledUrlChatIdRef.current = chatHistoryId;

    try {
      // Validate chat exists in history
      const isValid = selectChat(chatHistoryId);

      if (!isValid) {
        logger.error("Chat not found in history");

        return;
      }

      // Navigate to chat URL (IMMEDIATE - user requirement)
      navigateToChat(chatHistoryId, currentWorkflowId);

      // Load chat data from backend
      await load(adminClient, chatHistoryId);
      triggerResetPrompt();
    } catch (error: any) {
      logger.error("Failed to load chat", error);

      // Handle specific HTTP error codes
      if (error?.status === 403) {
        logger.error("You don't have access to this chat");
      } else if (error?.status === 404) {
        logger.error("Chat not found or has been deleted");
      } else {
        logger.error("Failed to load chat. Please try again.");
      }

      // Navigate back to base route on error
      const basePath =
        currentWorkflowId === "chat_agent" ? "/chat" : `/workflows/${currentWorkflowId}`;

      navigate(basePath, { replace: true });
    }
  };

  /**
   * Handler for search modal selections.
   * Unlike handleSelectChat, this uses selectAndEnsureVisible to handle
   * chats that may not be in the local cache (search results from API).
   */
  const handleSearchSelectChat = async (chatHistoryId: string) => {
    if (!adminClient) return;

    // Mark this chat ID as handled BEFORE navigation to prevent URL effect from re-processing
    lastHandledUrlChatIdRef.current = chatHistoryId;

    try {
      // Use selectAndEnsureVisible to fetch the chat if it's not in the displayed list
      // This is necessary for search results which may include chats not in local cache
      await selectAndEnsureVisible(chatHistoryId, adminClient);

      // Navigate to chat URL (IMMEDIATE - user requirement)
      navigateToChat(chatHistoryId, currentWorkflowId);

      // Load chat data from backend
      await load(adminClient, chatHistoryId);
      triggerResetPrompt();
    } catch (error: any) {
      logger.error("Failed to load chat from search", error);

      // Handle specific HTTP error codes
      if (error?.status === 403) {
        logger.error("You don't have access to this chat");
      } else if (error?.status === 404) {
        logger.error("Chat not found or has been deleted");
      } else {
        logger.error("Failed to load chat. Please try again.");
      }

      // Navigate back to base route on error
      const basePath =
        currentWorkflowId === "chat_agent" ? "/chat" : `/workflows/${currentWorkflowId}`;

      navigate(basePath, { replace: true });
    }
  };

  const handleNewChat = () => {
    logger.info("🆕 handleNewChat: Starting new chat");

    // Clear the chat ID from URL first
    clearChatId();
    logger.info("🆕 handleNewChat: URL cleared");

    // Create a new chat session
    newChat();
    logger.info("🆕 handleNewChat: newChat() called");

    // Clear the chat history sidebar selection
    clearSelection();
    logger.info("🆕 handleNewChat: Selection cleared");

    triggerResetPrompt();

    // Focus the chat input field after a short delay
    setTimeout(() => {
      const textarea = document.querySelector(
        'textarea[placeholder*="Start new chat"]',
      ) as HTMLTextAreaElement;

      if (textarea) {
        textarea.focus();
        logger.info("🆕 handleNewChat: Chat input focused");
      }
    }, 150);

    // Fetch chat history again to refresh the list (after clearing selection)
    // Use setTimeout to ensure clearSelection() completes first
    if (adminClient && user?.id) {
      setTimeout(() => {
        logger.info("🆕 handleNewChat: Fetching chat history after clear");
        fetchChatHistory(user.id, adminClient);
      }, 10); // Increased from 0 to 10ms
    }
  };

  // const searchInput = (
  //   <Input
  //     aria-label={t("search")}
  //     classNames={{
  //       inputWrapper: "bg-default-100",
  //       input: "text-sm",
  //     }}
  //     data-id="search-input"
  //     endContent={
  //       <Kbd className="hidden lg:inline-block" keys={["command"]}>
  //         K
  //       </Kbd>
  //     }
  //     labelPlacement="outside"
  //     placeholder={`${t("search")}…`}
  //     startContent={
  //       <SearchIcon className="text-base text-default-400 pointer-events-none flex-shrink-0" />
  //     }
  //     type="search"
  //   />
  // );

  // Use secure sidebar menu items that validate permissions server-side
  const { menuItems: sidebarMenuItems, isValidating } = useSidebarMenuItems();

  logger.info("SidebarLayout - Menu items", {
    menuItemsCount: sidebarMenuItems.length,
    isValidating: String(isValidating),
  });
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onOpenChange: onModalOpenChange,
  } = useDisclosure();
  const {
    isOpen: isHelpModalOpen,
    onOpen: onHelpModalOpen,
    onOpenChange: onHelpModalOpenChange,
  } = useDisclosure();

  const { brand } = useBrand();
  const { theme, toggleTheme, isDark } = useTheme();

  const BrandIcon = theme === "dark" && brand.iconDark ? brand.iconDark : brand.icon;
  const SmallBrandIcon =
    theme === "dark" && brand.smallIconDark ? brand.smallIconDark : brand.smallIcon || BrandIcon;

  // function RecentPromptDropdown() {
  //   return (
  //     <Dropdown>
  //       <DropdownTrigger>
  //         <Icon
  //           className="text-default-500 opacity-0 group-hover:opacity-100"
  //           icon="solar:menu-dots-bold"
  //           width={24}
  //         />
  //       </DropdownTrigger>
  //       <DropdownMenu aria-label="Dropdown menu with icons" className="py-2" variant="faded">
  //         <DropdownItem
  //           key="share"
  //           className="text-default-500 data-[hover=true]:text-default-500"
  //           startContent={
  //             <Icon
  //               className="text-default-300"
  //               height={20}
  //               icon="solar:square-share-line-linear"
  //               width={20}
  //             />
  //           }
  //         >
  //           Share
  //         </DropdownItem>
  //         <DropdownItem
  //           key="rename"
  //           className="text-default-500 data-[hover=true]:text-default-500"
  //           startContent={
  //             <Icon className="text-default-300" height={20} icon="solar:pen-linear" width={20} />
  //           }
  //         >
  //           Rename
  //         </DropdownItem>
  //         <DropdownItem
  //           key="archive"
  //           className="text-default-500 data-[hover=true]:text-default-500"
  //           startContent={
  //             <Icon
  //               className="text-default-300"
  //               height={20}
  //               icon="solar:folder-open-linear"
  //               width={20}
  //             />
  //           }
  //         >
  //           Archive
  //         </DropdownItem>
  //         <DropdownItem
  //           key="delete"
  //           className="text-danger-500 data-[hover=true]:text-danger-500"
  //           color="danger"
  //           startContent={
  //             <Icon
  //               className="text-danger-500"
  //               height={20}
  //               icon="solar:trash-bin-minimalistic-linear"
  //               width={20}
  //             />
  //           }
  //         >
  //           Delete
  //         </DropdownItem>
  //       </DropdownMenu>
  //     </Dropdown>
  //   );
  // }

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const feedback = formData.get("feedback") as string;

    try {
      if (!adminClient) {
        throw new Error("Admin client not found");
      }
      await adminClient.feedback.createFeedback({
        feedback,
      });
      onModalOpenChange();
    } catch (error) {
      logger.error("Failed to submit feedback", error);
    }
  };

  return (
    <div className="flex h-dvh w-full">
      {isAuthenticated && brand.name && userProfile?.name && (
        <div
          className={cn(
            "relative flex h-full w-65 flex-col !border-r-small border-divider transition-width duration-150 ease-in-out",
            {
              "w-16 items-center px-2": isSidebarCompact,
            },
          )}
          style={{ transitionProperty: "width", willChange: "width" }}
        >
          {/* Sidebar header: logo and toggle button with smooth transition */}
          {isSidebarCompact ? (
            // Collapsed: logo on top, toggle button below
            <div className="flex flex-col items-center pt-4 gap-2">
              {/* Logo */}
              <Link
                aria-label="Home"
                className="flex items-center justify-center"
                color="foreground"
                href="/"
              >
                <SmallBrandIcon size={24} />
              </Link>
              {/* Toggle Button */}
              <Button
                isIconOnly
                aria-label="Expand sidebar"
                className="!m-0"
                size="sm"
                variant="light"
                onPress={toggleSidebarCompact}
              >
                <Icon
                  color={isDark ? brand.colors.dark.primary : brand.colors.light.primary}
                  height={20}
                  icon="solar:sidebar-minimalistic-linear"
                  width={20}
                />
              </Button>
            </div>
          ) : (
            // Expanded: logo left, toggle right
            <div className="flex items-center justify-between p-4 w-full">
              <Link aria-label="Home" className="flex items-center p-1" color="foreground" href="/">
                <SmallBrandIcon size={24} />
              </Link>
              {/* <Button
              isIconOnly
              aria-label="Collapse sidebar"
              variant="light"
              onPress={toggleSidebarCompact}
              className="!m-0"
            >
            </Button> */}
              <Icon
                color={isDark ? brand.colors.dark.primary : brand.colors.light.primary}
                height={24}
                icon="solar:sidebar-minimalistic-linear"
                style={{ cursor: "pointer" }}
                width={24}
                onClick={toggleSidebarCompact}
              />
            </div>
          )}
          {/* End sidebar header */}
          {showChatHistory && !isSidebarCompact && (
            <div className="flex justify-center items-start  flex-col">
              <Button
                aria-label="Back to menu"
                className="px-0 text-sm gap-0 justify-start"
                variant="light"
                onPress={handleBackToMenu}
              >
                <Icon icon="solar:alt-arrow-left-linear" width={24} />
                <span>{t("Back")}</span>
              </Button>
              <div className="w-full p-4 flex flex-col gap-2">
                <Button
                  className="btn-brand-primary h-[36px] w-full text-sm"
                  data-id="new-chat-button"
                  size="sm"
                  startContent={
                    <Icon
                      className="text-default-50"
                      icon="solar:chat-round-dots-linear"
                      width={20}
                    />
                  }
                  onPress={handleNewChat}
                >
                  {t("New Chat")}
                </Button>
              </div>
            </div>
          )}
          <div className="flex-1 flex flex-col min-h-0">
            <Sidebar
              className="px-4 py-2"
              defaultSelectedKey="home"
              isCompact={isSidebarCompact}
              itemClasses={{
                base: !isSidebarCompact ? "mb-2" : "",
                title: "text-brand-primary font-bold dark:!text-foreground",
              }}
              items={sidebarMenuItems}
              selectedKeys={[currentPath]}
              showMenuTitle={false}
            />
            {isSidebarCompact && showChatHistory && displayedChats.length > 0 && (
              <Tooltip content={t("Search chat history")} placement="right">
                <Button
                  isIconOnly
                  aria-label={t("Search chat history")}
                  className="btn-brand-primary mx-4 h-10 mb-2"
                  data-id="compact-search-button"
                  size="md"
                  onPress={onSearchModalOpen}
                >
                  <Icon className="text-brand-secondary" icon="solar:magnifer-linear" width={20} />
                </Button>
              </Tooltip>
            )}
            {!isSidebarCompact && showChatHistory && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex items-center justify-between px-[30px] py-2 flex-shrink-0">
                  <span className="font-bold text-brand-primary text-sm dark:!text-foreground">
                    {t("Chat History")}
                  </span>
                  <SearchChatButton
                    isCompact={true}
                    isDisabled={displayedChats.length === 0}
                    onOpenSearch={onSearchModalOpen}
                  />
                </div>
                <div className="flex-1 min-h-0">
                  <ChatHistoryComponent
                    isShowHistory={true}
                    sectionClasses={{
                      base: "px-4",
                    }}
                    showScroll={true}
                    onNewChat={handleNewChat}
                    onSelectChat={handleSelectChat}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            className={cn("mt-auto flex flex-col flex-shrink-0", {
              "items-center": isSidebarCompact,
            })}
            data-id="sidebar-footer-container"
          >
            {userProfile?.name && (
              <div className="flex flex-col gap-4 mb-4 px-4" data-id="user-profile-container">
                <Dropdown data-id="user-dropdown" placement="top-end">
                  <DropdownTrigger>
                    <Button
                      fullWidth
                      className={`min-h-[60px] h-auto justify-start gap- bg-transparent px-0 py-[10px] whitespace-normal ${isSidebarCompact ? "justify-center border-0" : ""}`}
                      data-id="user-dropdown-button"
                    >
                      <div
                        className={`flex w-full items-center gap-2 ${isSidebarCompact && "justify-center"}`}
                        data-id="user-info"
                      >
                        <Avatar
                          className="flex-shrink-0 w-7 h-7 text-tiny"
                          data-id="user-avatar"
                          src={userProfile?.picture}
                        />
                        {!isSidebarCompact && (
                          <p className="text-xs text-foreground break-words" data-id="user-name">
                            {userProfile?.name}
                          </p>
                        )}
                        {/* <div className="flex flex-col text-left" data-id="user-details">
                            <p className="text-xs text-foreground break-words" data-id="user-name">
                              {userProfile?.name}
                            </p>
                             <p className="text-tiny text-default-400" data-id="user-email">
                              {userProfile?.email}
                            </p> 
                          </div>*/}
                      </div>
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="Profile Actions"
                    className="w-[210px] bg-content1 px-[8px] py-[8px]"
                    data-id="profile-dropdown-menu"
                    variant="flat"
                  >
                    {/* <DropdownSection
                        showDivider
                        aria-label="profile-section-1"
                        className="px-0"
                        data-id="dropdown-section-1"
                      >
                        <DropdownItem
                          key="my-plan"
                          className="py-[4px] text-default-500"
                          data-id="dropdown-item-my-plan"
                          textValue="My Plan"
                        >
                          {t("My Plan")}
                        </DropdownItem>
                        <DropdownItem
                          key="my-gpts"
                          className="py-[4px] text-default-500"
                          data-id="dropdown-item-my-gpts"
                          textValue="My GPTs"
                        >
                          {t("My GPTs")}
                        </DropdownItem>
                      </DropdownSection> */}
                    <DropdownSection
                      aria-label="profile-section-3"
                      className="mb-0"
                      data-id="dropdown-section-3"
                    >
                      <DropdownItem
                        key="theme-switch"
                        className="pt-[4px] text-default-500"
                        closeOnSelect={false}
                        data-id="dropdown-item-theme-switch"
                        textValue="Theme Switch"
                        onPress={() => {
                          toggleTheme();
                        }}
                      >
                        <Tooltip content="Theme Switch" placement="right">
                          <ThemeSwitch data-id="theme-switch" />
                        </Tooltip>
                      </DropdownItem>
                      <DropdownItem
                        key="model-info"
                        className="py-[4px] text-default-500"
                        data-id="dropdown-item-model-info"
                        textValue="Models Overview"
                        onPress={() => navigate("/models")}
                      >
                        <div className="flex items-center gap-2">
                          <ModelInfoIcon size={20} />
                          <span className="text-small font-medium text-default-500">
                            {t("Models Overview")}
                          </span>
                        </div>
                      </DropdownItem>

                      <DropdownItem
                        key="settings"
                        className={permissions.settings ? "py-[4px] text-default-500" : "hidden"}
                        data-id="dropdown-item-settings"
                        textValue="Settings"
                        onPress={() => permissions.settings && navigate("/settings")}
                      >
                        <div className="flex items-center gap-2">
                          <Icon icon="solar:settings-outline" width={20} />
                          <span className="text-small font-medium text-default-500">
                            {t("Settings")}
                          </span>
                        </div>
                      </DropdownItem>
                      <DropdownItem
                        key="admin"
                        className={permissions.settings ? "py-[4px] text-default-500" : "hidden"}
                        data-id="dropdown-item-admin"
                        textValue="Admin"
                        onPress={() => permissions.settings && navigate("/admin")}
                      >
                        <div className="flex items-center gap-2">
                          <Icon icon="solar:shield-outline" width={20} />
                          <span className="text-small font-medium text-default-500">
                            {t("Admin")}
                          </span>
                        </div>
                      </DropdownItem>
                      <DropdownItem
                        key="logout"
                        className="pt-[4px] text-default-500"
                        data-id="dropdown-item-logout"
                        textValue="Log Out"
                        onPress={() => logout()}
                      >
                        <div className="flex items-center gap-2">
                          <Icon icon="solar:logout-3-outline" width={20} />
                          <span className="text-small font-medium text-default-500">
                            {t("Log Out")}
                          </span>
                        </div>
                      </DropdownItem>
                    </DropdownSection>
                  </DropdownMenu>
                </Dropdown>
              </div>
            )}
            {/* <div
              data-id="theme-switch-container"
              className={`flex justify-start items-center px-4 mb-2`}
            >
              <ThemeSwitch data-id="theme-switch" />
            </div> */}

            <div className="px-4">
              <LanguageSwitch
                availableLanguages={availableLanguages}
                data-id="language-switch"
                icon={I18nIcon}
                isSidebarCompact={isSidebarCompact}
              />
            </div>
            {brand.showNeedHelp && (
              <Tooltip content="Need Help" isDisabled={!isSidebarCompact} placement="right">
                <Button
                  fullWidth
                  aria-label="Provide feedback"
                  className={cn(
                    "justify-start truncate text-default-500 data-[hover=true]:text-foreground",
                    {
                      "justify-center": isSidebarCompact,
                    },
                  )}
                  isIconOnly={isSidebarCompact}
                  startContent={
                    !isSidebarCompact && (
                      <Icon
                        className="flex-none text-default-500"
                        icon="solar:call-chat-rounded-linear"
                        width={28}
                      />
                    )
                  }
                  variant="light"
                  onPress={onHelpModalOpen}
                >
                  {isSidebarCompact ? (
                    <Icon
                      className="flex-none text-default-500"
                      icon="solar:call-chat-rounded-linear"
                      width={28}
                    />
                  ) : (
                    t("Need Help")
                  )}
                </Button>
              </Tooltip>
            )}
            <Tooltip content="Feedback" isDisabled={!isSidebarCompact} placement="right">
              <Button
                fullWidth
                aria-label="Provide feedback"
                className={cn(
                  "justify-start truncate text-default-500 data-[hover=true]:text-foreground",
                  {
                    "justify-center": isSidebarCompact,
                  },
                )}
                isIconOnly={isSidebarCompact}
                startContent={
                  !isSidebarCompact && (
                    <Icon
                      className="flex-none text-default-500"
                      icon="solar:question-circle-linear"
                      width={28}
                    />
                  )
                }
                variant="light"
                onPress={onModalOpen}
              >
                {isSidebarCompact ? (
                  <Icon
                    className="text-default-500"
                    icon="solar:question-circle-linear"
                    width={28}
                  />
                ) : (
                  t("Feedback")
                )}
              </Button>
            </Tooltip>

            <div className="flex flex-col gap-2">
              <br />
            </div>

            <Modal isOpen={isModalOpen} size="4xl" onOpenChange={onModalOpenChange}>
              <ModalContent>
                {(onClose: any) => (
                  <>
                    <ModalHeader className="flex flex-col gap-1">{t("Feedback")}</ModalHeader>
                    <ModalBody>
                      <form onSubmit={handleFeedbackSubmit}>
                        <Textarea
                          aria-label="Feedback input"
                          className="w-full"
                          label={t("Your Feedback")}
                          minRows={3}
                          name="feedback"
                          placeholder={t("Enter your feedback here...")}
                        />
                        <div className="flex justify-end gap-2 mt-4">
                          <Button
                            aria-label="Close feedback modal"
                            style={{
                              backgroundColor: isDark
                                ? brand.colors.dark.tertiary
                                : brand.colors.light.tertiary,
                              color: "white",
                            }}
                            variant="solid"
                            onPress={onClose}
                          >
                            {t("Close")}
                          </Button>
                          <Button
                            aria-label="Submit feedback"
                            className="btn-brand-primary"
                            type="submit"
                          >
                            {t("Submit")}
                          </Button>
                        </div>
                      </form>
                    </ModalBody>
                  </>
                )}
              </ModalContent>
            </Modal>

            <Modal isOpen={isHelpModalOpen} size="md" onOpenChange={onHelpModalOpenChange}>
              <ModalContent>
                <ModalHeader className="flex flex-col gap-1 text-lg font-bold text-primary">
                  {t("Need Help?")}
                </ModalHeader>
                <ModalBody>
                  <div className="flex flex-col gap-4 text-base text-default-700">
                    <div className="text-default-700">
                      {t(
                        "If you need assistance, our support team is here to help you. You can reach us through the following channel:",
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                      <Button
                        as="a"
                        className="w-full max-w-xs font-semibold text-base btn-brand-primary"
                        href={brand.helpUrl}
                        rel="noopener noreferrer"
                        startContent={
                          <Icon height={22} icon="solar:call-chat-rounded-linear" width={22} />
                        }
                        target="_blank"
                      >
                        {t("Live Chat with InvestAI Support")}
                      </Button>
                      <div className="text-xs text-default-500 text-center mt-2">
                        {t("We are available and happy to assist you!")}
                      </div>
                    </div>
                  </div>
                </ModalBody>
              </ModalContent>
            </Modal>
          </div>
        </div>
      )}

      {/* Chat History Search Modal */}
      <ChatHistorySearchModal
        chatHistory={chatHistory}
        isOpen={isSearchModalOpen}
        onOpenChange={onSearchModalOpenChange}
        onSelectChat={handleSearchSelectChat}
      />

      <div className="w-full flex-1 flex-col overflow-hidden">
        <main className="h-full w-full overflow-auto">{children}</main>
      </div>
    </div>
  );
}
